import authService from './auth.service';
import type {
  WhatsAppByPhoneResponse,
  WhatsAppChatsListResponse,
  WhatsAppChat,
  Wazzup24Instance,
  WhatsAppCloseReason,
  WhatsAppClosedChat,
  WhatsAppSendParams,
  WhatsAppSendResponse,
  WABATemplate,
  WABATemplateListResponse,
  SendWABATemplateParams,
} from '../types/whatsapp.types';

class WhatsAppService {
  private readonly BASE = '/api/v1/integrations/whatsapp';
  private readonly PROVIDERS_BASE = '/api/v1/integrations/providers';
  private readonly DICTS_BASE = '/api/v1/dicts';

  // Cache for close reasons to avoid repeated API calls
  private closeReasonsCache: WhatsAppCloseReason[] | null = null;
  private closeReasonsCacheTime: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  async getByPhone(phone: string, params?: { instance_id?: number; limit?: number; offset?: number }): Promise<WhatsAppByPhoneResponse> {
    const q = new URLSearchParams();
    if (typeof params?.instance_id === 'number') q.set('instance_id', String(params.instance_id));
    q.set('limit', String(params?.limit ?? 50));
    if (typeof params?.offset === 'number') q.set('offset', String(params.offset));
    const url = `${this.BASE}/by-phone/${encodeURIComponent(phone)}?${q.toString()}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) throw new Error('Не удалось загрузить чат WhatsApp');
    return res.json();
  }

  /**
   * Унифицированный метод отправки сообщения WhatsApp
   *
   * Логика endpoint:
   * 1. Если whatsapp_chat_id указан -> использует существующий чат
   * 2. Иначе ищет чат по (instance_id, phone_number)
   * 3. Если чат не найден -> автоматически создаёт клиента и чат
   * 4. Отправляет сообщение (текст/медиа/кнопки)
   * 5. Broadcast через WebSocket
   */
  async send(params: WhatsAppSendParams): Promise<WhatsAppSendResponse> {
    const url = `${this.BASE}/send`;
    const formData = new FormData();

    // Обязательные параметры
    formData.append('phone_number', params.phone_number);
    formData.append('instance_id', String(params.instance_id));

    // Опциональные параметры
    if (typeof params.whatsapp_chat_id === 'number') {
      formData.append('whatsapp_chat_id', String(params.whatsapp_chat_id));
    }

    if (typeof params.customer_id === 'number') {
      formData.append('customer_id', String(params.customer_id));
    }

    if (typeof params.lead_id === 'number') {
      formData.append('lead_id', String(params.lead_id));
    }

    // Текст сообщения
    const trimmedText = (params.text || '').trim();
    if (trimmedText) {
      formData.append('text', trimmedText);
    }

    // Обработка файла
    if (params.file) {
      if (params.file.size === 0) {
        throw new Error('Файл пустой (0 байт). Попробуйте выбрать файл заново.');
      }

      // Verify file has actual content
      try {
        const arrayBuffer = await params.file.arrayBuffer();
        if (arrayBuffer.byteLength === 0) {
          throw new Error('Файл не содержит данных. PDF и документы нельзя вставлять через Ctrl+V, используйте кнопку прикрепления.');
        }
        // Create new File from verified data
        const verifiedFile = new File([arrayBuffer], params.file.name, {
          type: params.file.type,
          lastModified: params.file.lastModified
        });
        formData.append('file', verifiedFile);
      } catch (e: any) {
        if (e.message.includes('Файл не содержит')) throw e;
        throw new Error('Не удалось прочитать файл. Попробуйте выбрать через кнопку прикрепления.');
      }

      // Определяем media_type
      if (params.media_type) {
        formData.append('media_type', params.media_type);
      } else {
        // Auto-detect по MIME типу
        const mimeType = params.file.type.toLowerCase();
        let mediaType = 'document';
        if (mimeType.startsWith('image/')) mediaType = 'image';
        else if (mimeType.startsWith('video/')) mediaType = 'video';
        else if (mimeType.startsWith('audio/')) mediaType = 'audio';
        formData.append('media_type', mediaType);
      }
    }

    // Reply to message (используем внутренний ID, не external_message_id)
    if (typeof params.reply_to_message_id === 'number') {
      formData.append('reply_to_message_id', String(params.reply_to_message_id));
    }

    // Buttons JSON
    if (params.buttons_json) {
      formData.append('buttons_json', params.buttons_json);
    }

    // Проверка, что есть хотя бы что-то для отправки
    if (!trimmedText && !params.file) {
      throw new Error('Необходимо указать либо текст, либо файл');
    }

    const res = await authService.fetchWithAuthForm(url, {
      method: 'POST',
      body: formData,
    });

    if (!res.ok) {
      let errorMsg = 'Не удалось отправить сообщение';
      try {
        const errorData = await res.json();
        if (errorData.detail) {
          errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        }
      } catch {
        // Если не удалось распарсить JSON, используем дефолтное сообщение
      }
      throw new Error(errorMsg);
    }

    return res.json();
  }

  async markRead(chatId: number, messageIds: number[] | null = null): Promise<void> {
    const url = `${this.BASE}/chats/${chatId}/mark-read`;
    const res = await authService.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds }),
    });
    if (!res.ok) throw new Error('Не удалось пометить сообщения прочитанными');
  }

  async listChats(
    params: {
      instance_id: number;
      limit?: number;
      offset?: number;
      status?: string;
      sort_by?: string;
      order?: 'asc' | 'desc';
      chat_id?: string;
    },
    signal?: AbortSignal
  ): Promise<WhatsAppChatsListResponse> {
    const q = new URLSearchParams();
    q.set('instance_id', String(params.instance_id));
    q.set('limit', String(params.limit ?? 20));
    q.set('offset', String(params.offset ?? 0));
    if (params.status) q.set('status', params.status);
    if (params.sort_by) q.set('sort_by', params.sort_by);
    if (params.order) q.set('order', params.order);
    if (params.chat_id && params.chat_id.trim()) q.set('chat_id', params.chat_id.trim());
    const url = `${this.BASE}/chats?${q.toString()}`;
    const res = await authService.fetchWithAuth(url, { signal });
    if (!res.ok) throw new Error('Не удалось загрузить список чатов');
    return res.json();
  }

  async getChatById(id: number): Promise<WhatsAppChat> {
    const url = `${this.BASE}/chats/${id}`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) throw new Error('Не удалось загрузить чат');
    return res.json();
  }

  /**
   * Получить список доступных инстансов Wazzup24
   */
  async getWazzup24Instances(): Promise<Wazzup24Instance[]> {
    const url = `${this.PROVIDERS_BASE}/wazzup24/instances`;
    const res = await authService.fetchWithAuth(url);
    if (!res.ok) {
      throw new Error('Не удалось загрузить список инстансов Wazzup24');
    }
    return res.json();
  }

  /**
   * Создать новый инстанс Wazzup24
   */
  async createWazzup24Instance(params: { name: string; api_key: string; channel_id: string; logo?: File }): Promise<Wazzup24Instance> {
    const url = `${this.PROVIDERS_BASE}/wazzup24/instances`;
    const formData = new FormData();
    formData.append('name', params.name);
    formData.append('api_key', params.api_key);
    formData.append('channel_id', params.channel_id);
    if (params.logo) {
      formData.append('logo', params.logo);
    }

    const res = await authService.fetchWithAuthForm(url, {
      method: 'POST',
      body: formData,
    });

    if (!res.ok) {
      let errorMsg = 'Не удалось создать инстанс Wazzup24';
      try {
        const errorData = await res.json();
        if (errorData.detail) {
          errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        }
      } catch {
        // Если не удалось распарсить JSON, используем дефолтное сообщение
      }
      throw new Error(errorMsg);
    }

    return res.json();
  }

  /**
   * Получить список причин закрытия чата (только ручные причины)
   * Использует кеширование для оптимизации
   */
  async getCloseReasons(forceRefresh = false): Promise<WhatsAppCloseReason[]> {
    const now = Date.now();

    // Return cached if available and not expired
    if (!forceRefresh && this.closeReasonsCache && (now - this.closeReasonsCacheTime) < this.CACHE_TTL) {
      return this.closeReasonsCache;
    }

    const url = `${this.DICTS_BASE}/whatsapp_close_reasons/?is_system=false`;
    const res = await authService.fetchWithAuth(url);

    if (!res.ok) {
      throw new Error('Не удалось загрузить причины закрытия чата');
    }

    const reasons: WhatsAppCloseReason[] = await res.json();

    // Update cache
    this.closeReasonsCache = reasons;
    this.closeReasonsCacheTime = now;

    return reasons;
  }

  /**
   * Закрыть чат с указанием причины и опционального комментария
   */
  async closeChat(chatId: number, reasonId: number, comment?: string): Promise<WhatsAppClosedChat> {
    const url = `${this.BASE}/chats/${chatId}/close`;
    const payload: { reason_id: number; comment?: string } = { reason_id: reasonId };

    // Добавляем комментарий только если он не пустой (до 1000 символов)
    if (comment && comment.trim()) {
      payload.comment = comment.trim().slice(0, 1000);
    }

    const res = await authService.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let errorMsg = 'Не удалось закрыть чат';
      try {
        const errorData = await res.json();
        if (errorData.detail) {
          errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        }
      } catch {
        // Если не удалось распарсить JSON, используем дефолтное сообщение
      }
      throw new Error(errorMsg);
    }

    return res.json();
  }

  /**
   * Очистить кеш причин закрытия
   */
  clearCloseReasonsCache(): void {
    this.closeReasonsCache = null;
    this.closeReasonsCacheTime = 0;
  }

  // === WABA Templates ===

  /**
   * Получить список WABA шаблонов для instance
   * Возвращает только одобренные (APPROVED) шаблоны
   */
  async getWABATemplates(instanceId: number): Promise<WABATemplate[]> {
    const url = `${this.BASE}/templates?instance_id=${instanceId}`;
    const res = await authService.fetchWithAuth(url);

    if (!res.ok) {
      let errorMsg = 'Не удалось загрузить WABA шаблоны';
      try {
        const errorData = await res.json();
        if (errorData.detail) {
          errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        }
      } catch {
        // Если не удалось распарсить JSON, используем дефолтное сообщение
      }
      throw new Error(errorMsg);
    }

    const data: WABATemplateListResponse = await res.json();
    // Фильтруем только одобренные шаблоны (case-insensitive)
    return data.items.filter(t => t.status?.toUpperCase() === 'APPROVED');
  }

  /**
   * Отправить WABA template message
   */
  async sendWABATemplate(params: SendWABATemplateParams): Promise<WhatsAppSendResponse> {
    const url = `${this.BASE}/send-template`;
    const res = await authService.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(params),
    });

    if (!res.ok) {
      let errorMsg = 'Не удалось отправить template message';
      try {
        const errorData = await res.json();
        if (errorData.detail) {
          errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        }
      } catch {
        // Если не удалось распарсить JSON, используем дефолтное сообщение
      }
      throw new Error(errorMsg);
    }

    return res.json();
  }

  async repairWebhook(instanceId: number): Promise<{
    instance_id: number;
    was_set: boolean;
    current_url: string | null;
    repaired: boolean;
    message: string;
  }> {
    const url = `${this.PROVIDERS_BASE}/wazzup24/instances/${instanceId}/repair-webhook`;
    const res = await authService.fetchWithAuth(url, { method: 'POST' });
    if (!res.ok) {
      let errorMsg = 'Не удалось проверить webhook';
      try {
        const errorData = await res.json();
        if (errorData.detail) {
          errorMsg = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        }
      } catch {
        // ignore
      }
      throw new Error(errorMsg);
    }
    return res.json();
  }
}

const whatsappService = new WhatsAppService();
export default whatsappService;
